import { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowDown, ArrowUp, Loader2, Search, PlusCircle, Camera, X, CheckCircle2,
  Package, LogOut, KeyRound, ArrowLeft, Pencil, History, ChevronDown, ChevronRight,
  Warehouse, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

/**
 * Store — kiosk-style flow.
 * State machine: LOGIN → CHOOSE_DIR → SEARCH → MATCH_RESULT
 *              → (QUANTITY | NEW_ITEM → QUANTITY) → SUCCESS → CHOOSE_DIR
 */
const STEP = {
  LOGIN: "login",
  CHOOSE_DIR: "choose_dir",
  SEARCH: "search",
  MATCH_RESULT: "match_result",
  NEW_ITEM: "new_item",
  QUANTITY: "quantity",
  SUCCESS: "success",
};

export default function Store() {
  const { hasModule } = useAuth();
  const canManageEntries = hasModule("store.recent_entries");
  const canEditInventory = hasModule("store.inventory_edit");
  const canDeleteInventory = hasModule("store.inventory_delete");
  const canViewStock = hasModule("store.stock");
  const [step, setStep] = useState(STEP.LOGIN);
  const [showLiveInv, setShowLiveInv] = useState(false);
  const [employee, setEmployee] = useState(null); // {code, name}
  const [direction, setDirection] = useState(null); // INWARD | OUTWARD
  const [query, setQuery] = useState("");
  const [matchResult, setMatchResult] = useState(null); // { match, confidence }
  const [selectedItem, setSelectedItem] = useState(null); // final item to log against
  const [lastEntry, setLastEntry] = useState(null);

  const reset = (toStep = STEP.CHOOSE_DIR) => {
    setDirection(null);
    setQuery("");
    setMatchResult(null);
    setSelectedItem(null);
    setStep(toStep);
  };

  const logout = () => {
    setEmployee(null);
    reset(STEP.LOGIN);
  };

  const wideStep = step === STEP.SEARCH;   // both INWARD & OUTWARD show the full inventory table
  return (
    <div className="min-h-[calc(100vh-8rem)] flex items-start justify-center py-4">
      <div className={`w-full ${wideStep ? "max-w-[1600px] px-4 sm:px-6" : "max-w-2xl"}`}>
        {employee && step !== STEP.LOGIN && (
          <div className="flex items-center justify-between mb-6 px-1" data-testid="store-header">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Package className="h-4 w-4" />
              <span className="font-medium text-slate-900">{employee.name}</span>
              <span className="text-slate-400">·</span>
              <span className="font-mono">{employee.code}</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={logout}
              data-testid="store-logout-btn"
              className="text-slate-500 hover:text-slate-900"
            >
              <LogOut className="h-4 w-4 mr-1" /> Switch worker
            </Button>
          </div>
        )}

        {canViewStock && (
          <div className="mb-4 flex justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowLiveInv(true)}
              data-testid="store-live-inventory-btn"
            >
              <Warehouse className="h-4 w-4 mr-1.5" /> Live Inventory
            </Button>
          </div>
        )}
        {showLiveInv && (
          <LiveInventoryPage
            canEditInventory={canEditInventory}
            canDeleteInventory={canDeleteInventory}
            canManageEntries={canManageEntries}
            onClose={() => setShowLiveInv(false)}
          />
        )}

        {step === STEP.LOGIN && (
          <LoginStep
            onSuccess={(emp) => {
              setEmployee(emp);
              setStep(STEP.CHOOSE_DIR);
            }}
          />
        )}
        {step === STEP.CHOOSE_DIR && (
          <ChooseDirectionStep
            onPick={(d) => {
              setDirection(d);
              setStep(STEP.SEARCH);
            }}
          />
        )}
        {step === STEP.SEARCH && direction === "OUTWARD" && (
          <PickFromLiveInventoryStep
            canEdit={canEditInventory}
            canDelete={canDeleteInventory}
            onBack={() => setStep(STEP.CHOOSE_DIR)}
            onPick={(item) => {
              setMatchResult(null);
              setSelectedItem(item);
              setStep(STEP.QUANTITY);
            }}
          />
        )}
        {step === STEP.SEARCH && direction !== "OUTWARD" && (
          <div className="space-y-6">
            <SearchStep
              direction={direction}
              query={query}
              setQuery={setQuery}
              onBack={() => setStep(STEP.CHOOSE_DIR)}
              onMatched={(res) => {
                setMatchResult(res);
                setStep(STEP.MATCH_RESULT);
              }}
              onPickFromList={(item) => {
                setMatchResult(null);
                setSelectedItem(item);
                setStep(STEP.QUANTITY);
              }}
            />
            {/* Full Live Inventory below the search card so the user can
                scroll & browse existing items while they type. Read-only. */}
            <div className="pt-2 border-t border-slate-200">
              <div className="mb-2">
                <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Store</p>
                <h3 className="font-heading text-lg font-bold text-slate-900">Live Inventory</h3>
              </div>
              <LiveInventoryFull canEdit={canEditInventory} canDelete={canDeleteInventory} />
            </div>
          </div>
        )}
        {step === STEP.MATCH_RESULT && (
          <MatchResultStep
            query={query}
            direction={direction}
            matchResult={matchResult}
            onConfirm={(item) => {
              setSelectedItem(item);
              setStep(STEP.QUANTITY);
            }}
            onReject={() => setStep(STEP.NEW_ITEM)}
            onBack={() => setStep(STEP.SEARCH)}
          />
        )}
        {step === STEP.NEW_ITEM && (
          <NewItemStep
            initialName={query}
            employeeCode={employee.code}
            onCreated={(item) => {
              setSelectedItem(item);
              setStep(STEP.QUANTITY);
            }}
            onBack={() => setStep(STEP.MATCH_RESULT)}
          />
        )}
        {step === STEP.QUANTITY && (
          <QuantityStep
            item={selectedItem}
            direction={direction}
            employeeCode={employee.code}
            onSubmitted={(entry) => {
              setLastEntry(entry);
              setStep(STEP.SUCCESS);
            }}
            onBack={() => {
              // Back either to match result or to search
              setStep(matchResult ? STEP.MATCH_RESULT : STEP.NEW_ITEM);
            }}
          />
        )}
        {step === STEP.SUCCESS && (
          <SuccessStep
            entry={lastEntry}
            onNext={() => reset(STEP.CHOOSE_DIR)}
          />
        )}
      </div>
    </div>
  );
}

// ------------------ Step 1: Login ------------------
function LoginStep({ onSuccess }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      const { data } = await ax.post(`${API}/store/verify-employee`, { code: trimmed });
      toast.success(`Welcome, ${data.name}`);
      onSuccess(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unknown employee code";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="store-login-card">
      <CardContent className="p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-900 text-white">
            <KeyRound className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">Store — Sign In</h1>
          <p className="text-slate-500 text-sm">Enter your Employee Code to continue</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="emp-code" className="text-sm font-medium text-slate-700">
              Employee Code
            </Label>
            <Input
              id="emp-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. EMP-042"
              className="h-14 text-2xl text-center font-mono tracking-widest"
              data-testid="store-emp-code-input"
              inputMode="text"
            />
          </div>
          <Button
            type="submit"
            className="w-full h-14 text-lg"
            disabled={loading || !code.trim()}
            data-testid="store-emp-submit-btn"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Continue"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ------------------ Step 2: Choose direction ------------------
function ChooseDirectionStep({ onPick }) {
  return (
    <div className="space-y-6" data-testid="store-choose-dir">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <button
          onClick={() => onPick("INWARD")}
          data-testid="store-inward-btn"
          className="group flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors py-16 px-6"
        >
          <div className="h-20 w-20 rounded-full bg-emerald-600 text-white flex items-center justify-center group-hover:scale-105 transition-transform">
            <ArrowDown className="h-10 w-10" />
          </div>
          <div className="text-center">
            <div className="text-3xl font-semibold text-emerald-900">INWARD</div>
            <div className="text-sm text-emerald-700 mt-1">Receiving new stock</div>
          </div>
        </button>
        <button
          onClick={() => onPick("OUTWARD")}
          data-testid="store-outward-btn"
          className="group flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-orange-200 bg-orange-50 hover:bg-orange-100 transition-colors py-16 px-6"
        >
          <div className="h-20 w-20 rounded-full bg-orange-600 text-white flex items-center justify-center group-hover:scale-105 transition-transform">
            <ArrowUp className="h-10 w-10" />
          </div>
          <div className="text-center">
            <div className="text-3xl font-semibold text-orange-900">OUTWARD</div>
            <div className="text-sm text-orange-700 mt-1">Consuming stock</div>
          </div>
        </button>
      </div>
    </div>
  );
}

// Live inventory table shown right after the worker logs in.
// Displays each item's current balance, last-received photo thumbnail and
// last-known location so the operator can eyeball stock before adding /
// consuming more. Fully read-only for kiosk usage.
function LiveInventoryPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [zoomPhoto, setZoomPhoto] = useState(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  // Photo URLs come in two flavours: (1) a full inline data-URI like
  // "data:image/jpeg;base64,..." — bulletproof, no server round-trip — or
  // (2) a legacy relative path "/api/store/photo/<uuid>.png" which needs the
  // backend prefix. Distinguish before rendering so we don't double-prefix
  // data-URIs (which would break the <img>).
  const resolvePhoto = (u) => (u && u.startsWith("data:") ? u : `${BACKEND}${u}`);

  useEffect(() => {
    let dead = false;
    ax.get(`${API}/store/balance`)
      .then(({ data }) => { if (!dead) setRows(Array.isArray(data) ? data : []); })
      .catch(() => {})
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, []);

  const filtered = filter.trim()
    ? rows.filter((r) => (r.item_name || "").toLowerCase().includes(filter.toLowerCase().trim()))
    : rows;

  return (
    <Card className="border-slate-200" data-testid="store-live-inventory">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Live inventory</p>
            <p className="text-sm text-slate-800 font-heading font-bold">Current stock on hand</p>
          </div>
          <div className="flex items-center gap-2 bg-slate-100 rounded-sm px-2 py-1 w-56">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter items"
              className="bg-transparent border-none outline-none text-sm w-full"
              data-testid="store-live-inventory-filter"
            />
          </div>
        </div>
        {loading ? (
          <div className="text-sm text-slate-500 py-4 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">No items yet.</p>
        ) : (
          <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-[10px] tracking-widest uppercase text-slate-500">
                  <th className="text-left px-3 py-1.5 font-semibold">Item</th>
                  <th className="text-right px-3 py-1.5 font-semibold">Balance</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Location</th>
                  <th className="text-center px-3 py-1.5 font-semibold">Photo</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.item_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`store-inv-row-${r.item_id}`}>
                    <td className="px-3 py-2 text-slate-900">{r.item_name}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      <span className={r.balance <= 0 ? "text-red-700 font-semibold" : "text-slate-900"}>
                        {r.balance}
                      </span>
                      <span className="text-slate-400 ml-1">{r.unit}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-[11px]">{r.last_location || "—"}</td>
                    <td className="px-3 py-2 text-center">
                      {r.last_photo_url ? (
                        <button
                          type="button"
                          onClick={() => setZoomPhoto(resolvePhoto(r.last_photo_url))}
                          className="inline-block"
                          data-testid={`store-inv-photo-${r.item_id}`}
                        >
                          <img
                            src={resolvePhoto(r.last_photo_url)}
                            alt={r.item_name}
                            className="h-8 w-8 object-cover rounded-sm border border-slate-200 mx-auto hover:ring-2 hover:ring-slate-400"
                          />
                        </button>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {zoomPhoto && (
        <div
          onClick={() => setZoomPhoto(null)}
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-pointer"
          data-testid="store-photo-zoom"
        >
          <img src={zoomPhoto} alt="preview" className="max-h-[90vh] max-w-[90vw] object-contain rounded-sm" />
        </div>
      )}
    </Card>
  );
}

// ------------------ Step 3: Search ------------------
function SearchStep({ direction, query, setQuery, onBack, onMatched, onPickFromList }) {
  const [loading, setLoading] = useState(false);
  const [stock, setStock] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const isOutward = direction === "OUTWARD";

  // Load stock list (only for OUTWARD flow)
  useEffect(() => {
    if (!isOutward) return;
    let cancelled = false;
    setStockLoading(true);
    ax.get(`${API}/store/balance`)
      .then(({ data }) => {
        if (!cancelled) setStock(Array.isArray(data) ? data : []);
      })
      .catch(() => { if (!cancelled) toast.error("Could not load stock list"); })
      .finally(() => { if (!cancelled) setStockLoading(false); });
    return () => { cancelled = true; };
  }, [isOutward]);

  const submit = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    try {
      const { data } = await ax.post(`${API}/store/ai-match`, { query: q });
      onMatched(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Match failed");
    } finally {
      setLoading(false);
    }
  };

  // Filter stock rows by the search text and hide out-of-stock items on OUTWARD
  const visibleStock = stock
    .filter((r) => r.balance > 0)
    .filter((r) => {
      const q = filter.trim().toLowerCase();
      if (!q) return true;
      return (r.item_name || "").toLowerCase().includes(q);
    });

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="store-search-card">
      <CardContent className="p-6 sm:p-8 space-y-6">
        <DirectionBadge direction={direction} />
        <div className="space-y-2">
          <Label htmlFor="q" className="text-lg font-medium text-slate-900">
            {isOutward ? "Which item are you taking?" : "What are you logging?"}
          </Label>
          <p className="text-sm text-slate-500">
            {isOutward
              ? "Tap an item below, or type its name to search."
              : "Type the item name — spelling doesn't matter, we'll match it."}
          </p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              id="q"
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (isOutward) setFilter(e.target.value);
              }}
              placeholder={isOutward ? "Filter or type to search" : "e.g. blue tape, oil grease, cotton waste"}
              className="h-14 text-lg pl-12"
              data-testid="store-search-input"
            />
          </div>

          {isOutward && (
            <div className="rounded-lg border border-slate-200 bg-white" data-testid="store-outward-list">
              <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between text-xs uppercase tracking-wider text-slate-500">
                <span>Available Stock</span>
                <span>{visibleStock.length} items</span>
              </div>
              {stockLoading ? (
                <div className="p-6 text-center text-sm text-slate-500">
                  <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                  Loading stock…
                </div>
              ) : visibleStock.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500">
                  {stock.length === 0 ? "No items in the store yet." : "No items match your search."}
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                  {visibleStock.map((r) => (
                    <button
                      key={r.item_id}
                      type="button"
                      onClick={() => onPickFromList({ id: r.item_id, name: r.item_name, unit: r.unit })}
                      data-testid={`store-outward-item-${r.item_id}`}
                      className="w-full text-left px-4 py-3 hover:bg-orange-50 focus:bg-orange-50 focus:outline-none transition-colors flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="text-base font-medium text-slate-900 truncate">{r.item_name}</p>
                        <p className="text-xs text-slate-500">{r.unit}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xl font-mono font-semibold text-orange-700 tabular-nums">
                          {formatBalance(r.balance)}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-slate-400">in stock</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="h-14 px-6"
              data-testid="store-back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Button
              type="submit"
              className="flex-1 h-14 text-lg"
              disabled={loading || !query.trim()}
              data-testid="store-search-btn"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (isOutward ? "Or use AI search" : "Search")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function formatBalance(n) {
  const num = Number(n) || 0;
  return num % 1 === 0 ? String(num) : num.toFixed(2);
}

// ------------------ Step 4: Match result ------------------
function MatchResultStep({ query, direction, matchResult, onConfirm, onReject, onBack }) {
  const match = matchResult?.match;
  const isOutward = direction === "OUTWARD";
  // OUTWARD can only use existing SKUs — no "add new SKU" allowed.
  const allowNewSku = !isOutward;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="store-match-card">
      <CardContent className="p-6 sm:p-8 space-y-6">
        <DirectionBadge direction={direction} />
        <div className="space-y-1">
          <p className="text-sm text-slate-500">You typed</p>
          <p className="text-lg font-mono text-slate-700 italic">"{query}"</p>
        </div>

        {match ? (
          <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-6 space-y-2">
            <p className="text-sm text-emerald-700 font-medium uppercase tracking-wide">Did you mean</p>
            <p className="text-3xl font-semibold text-emerald-900" data-testid="store-match-name">
              {match.name}
            </p>
            <p className="text-emerald-700">
              Unit: <span className="font-mono">{match.unit}</span>
              {matchResult?.confidence != null && (
                <span className="ml-3 text-xs text-emerald-600">
                  confidence {matchResult.confidence}%
                </span>
              )}
            </p>
          </div>
        ) : (
          <div className="rounded-lg border-2 border-slate-200 bg-slate-50 p-6">
            <p className="text-slate-700">
              {isOutward
                ? "No matching item found. Go back and pick an item from the stock list, or search again with a different name."
                : "No matching item found. If it's a new SKU, add it below."}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {match && (
            <Button
              onClick={() => onConfirm(match)}
              className={`h-14 text-lg bg-emerald-600 hover:bg-emerald-700 ${!allowNewSku ? "sm:col-span-2" : ""}`}
              data-testid="store-confirm-btn"
            >
              <CheckCircle2 className="h-5 w-5 mr-2" /> Confirm
            </Button>
          )}
          {allowNewSku && (
            <Button
              onClick={onReject}
              variant="outline"
              className={`h-14 text-lg border-slate-300 ${!match ? "sm:col-span-2" : ""}`}
              data-testid="store-new-sku-btn"
            >
              <PlusCircle className="h-5 w-5 mr-2" /> No, this is a new SKU
            </Button>
          )}
        </div>
        <button
          onClick={onBack}
          className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"
          data-testid="store-back-to-search"
        >
          <ArrowLeft className="h-4 w-4" /> Search again
        </button>
      </CardContent>
    </Card>
  );
}

// ------------------ Step 5: New item ------------------
function NewItemStep({ initialName, employeeCode, onCreated, onBack }) {
  const [name, setName] = useState(initialName || "");
  const [unit, setUnit] = useState("");
  const [category, setCategory] = useState("");
  const [specs, setSpecs] = useState([]);          // [{type, unit, value}]
  const [loading, setLoading] = useState(false);
  const [cats, setCats] = useState([]);            // store_category master
  const [specTypes, setSpecTypes] = useState([]);  // store_spec_type master
  const [specUnits, setSpecUnits] = useState([]);  // store_spec_unit master

  useEffect(() => {
    // Load the three spec-related masters once. Non-blocking — the form still
    // works even if the masters are empty (dropdowns just show a placeholder).
    const loadMaster = async (type, setter) => {
      try {
        const { data } = await ax.get(`${API}/masters`, { params: { master_type: type, status: "active" } });
        setter((data || []).filter((m) => m.active !== false).map((m) => m.name));
      } catch { /* silent */ }
    };
    loadMaster("store_category", setCats);
    loadMaster("store_spec_type", setSpecTypes);
    loadMaster("store_spec_unit", setSpecUnits);
  }, []);

  const addSpec = () => setSpecs((s) => [...s, { type: "", unit: "", value: "" }]);
  const rmSpec  = (i) => setSpecs((s) => s.filter((_, idx) => idx !== i));
  const updSpec = (i, k, v) => setSpecs((s) => s.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || !unit.trim()) return;
    setLoading(true);
    try {
      const cleanSpecs = specs.filter((s) => (s.value || "").toString().trim());
      const { data } = await ax.post(`${API}/store/items`, {
        name: name.trim(),
        unit: unit.trim(),
        category: category.trim(),
        additional_specs: cleanSpecs,
        employee_code: employeeCode,
      });
      if (data.existed) toast.info(`Using existing item: ${data.name}`);
      else toast.success(`Created new item: ${data.name}`);
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create item");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="store-new-item-card">
      <CardContent className="p-6 sm:p-8 space-y-6">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-slate-900">Add a new item</h2>
          <p className="text-sm text-slate-500">
            This will be saved to the Non-Steel Purchase Master.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-name">Item name</Label>
            <Input id="new-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Blue Masking Tape 2 inch" className="h-12 text-lg"
              data-testid="store-new-item-name" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="new-unit">Unit of measurement</Label>
              <Input id="new-unit" value={unit} onChange={(e) => setUnit(e.target.value)}
                placeholder="e.g. rolls, kg, pieces" className="h-12 text-lg" data-testid="store-new-item-unit" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-category">Category</Label>
              <select id="new-category" value={category} onChange={(e) => setCategory(e.target.value)}
                data-testid="store-new-item-category"
                className="w-full h-12 px-3 border border-slate-300 rounded-md text-lg bg-white">
                <option value="">— None —</option>
                {cats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {cats.length === 0 && <p className="text-[11px] text-amber-700">Add categories under Masters → Store Category</p>}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Additional specs (optional)</Label>
              <Button type="button" size="sm" variant="outline" onClick={addSpec} data-testid="store-new-item-add-spec">
                + Add spec
              </Button>
            </div>
            {specs.length === 0 && (
              <p className="text-xs text-slate-500">e.g. Thickness · mm · 120  ·  Length · m · 25</p>
            )}
            {specs.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2" data-testid={`store-new-item-spec-${i}`}>
                <select value={r.type} onChange={(e) => updSpec(i, "type", e.target.value)}
                  className="h-10 px-2 border border-slate-300 rounded-sm text-sm bg-white">
                  <option value="">Type…</option>
                  {specTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={r.unit} onChange={(e) => updSpec(i, "unit", e.target.value)}
                  className="h-10 px-2 border border-slate-300 rounded-sm text-sm bg-white">
                  <option value="">Unit…</option>
                  {specUnits.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <Input value={r.value} onChange={(e) => updSpec(i, "value", e.target.value)}
                  placeholder="Value" className="h-10" />
                <button type="button" onClick={() => rmSpec(i)}
                  className="h-10 w-10 text-red-600 hover:bg-red-50 rounded-sm">×</button>
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onBack} className="h-14 px-6" data-testid="store-new-item-back">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Button type="submit" className="flex-1 h-14 text-lg"
              disabled={loading || !name.trim() || !unit.trim()} data-testid="store-new-item-create-btn">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create & Continue"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ------------------ Step 6: Quantity + optional photo ------------------
function QuantityStep({ item, direction, employeeCode, onSubmitted, onBack }) {
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [location, setLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const isInward = direction === "INWARD";

  // Fetch known locations for the datalist suggestions.
  useEffect(() => {
    if (!isInward) return;
    let dead = false;
    ax.get(`${API}/store/locations`)
      .then(({ data }) => { if (!dead) setLocations(Array.isArray(data) ? data : []); })
      .catch(() => {});
    return () => { dead = true; };
  }, [isInward]);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Photo too large (max 8 MB)");
      return;
    }
    setPhoto(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const clearPhoto = () => {
    setPhoto(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async (e) => {
    e?.preventDefault();
    const n = parseFloat(qty);
    if (!(n > 0)) {
      toast.error("Enter a valid quantity");
      return;
    }
    if (isInward && !location.trim()) {
      toast.error("Location is required for inward entries");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("item_id", item.id);
      fd.append("direction", direction);
      fd.append("quantity", String(n));
      fd.append("employee_code", employeeCode);
      if (isInward && location.trim()) fd.append("location", location.trim());
      if (note.trim()) fd.append("note", note.trim());
      if (photo) fd.append("photo", photo);
      const { data } = await ax.post(`${API}/store/entries`, fd);
      if (data.warning) toast.warning(data.warning);
      onSubmitted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save entry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="store-qty-card">
      <CardContent className="p-6 sm:p-8 space-y-6">
        <DirectionBadge direction={direction} />
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs text-slate-500 uppercase font-medium tracking-wide">Item</p>
          <p className="text-2xl font-semibold text-slate-900" data-testid="store-qty-item-name">
            {item.name}
          </p>
          <p className="text-sm text-slate-500">
            Unit: <span className="font-mono">{item.unit}</span>
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="qty" className="text-lg font-medium">
              Quantity ({item.unit})
            </Label>
            <Input
              id="qty"
              type="number"
              min="0"
              step="any"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder="0"
              className="h-16 text-3xl text-center font-mono"
              data-testid="store-qty-input"
              autoFocus
              inputMode="decimal"
            />
          </div>

          {isInward && (
            <div className="space-y-2">
              <Label htmlFor="location" className="text-sm font-medium">
                Location <span className="text-red-600">*</span>
              </Label>
              <Input
                id="location"
                list="store-locations-list"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Rack A / Row 3 / Bin 12…"
                className="h-12 text-base"
                data-testid="store-location-input"
                required
              />
              <datalist id="store-locations-list">
                {locations.map((l) => (
                  <option key={l.location} value={l.location} />
                ))}
              </datalist>
              <p className="text-[11px] text-slate-500">
                Type freely. Previously-used locations appear as suggestions.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-sm font-medium">Photo (optional)</Label>
            {!photoPreview ? (
              <label
                htmlFor="photo-input"
                className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 py-8 cursor-pointer transition-colors"
                data-testid="store-photo-picker"
              >
                <Camera className="h-8 w-8 text-slate-400" />
                <span className="text-sm text-slate-600">Tap to take a photo</span>
                <input
                  id="photo-input"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={onPhoto}
                  className="hidden"
                />
              </label>
            ) : (
              <div className="relative">
                <img
                  src={photoPreview}
                  alt="preview"
                  className="rounded-lg w-full max-h-64 object-contain bg-slate-100"
                  data-testid="store-photo-preview"
                />
                <button
                  type="button"
                  onClick={clearPhoto}
                  className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"
                  data-testid="store-photo-clear-btn"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="note" className="text-sm font-medium">
              Note (optional)
            </Label>
            <Input
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. supplier invoice #, bay location"
              className="h-11"
              data-testid="store-note-input"
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="h-14 px-6"
              data-testid="store-qty-back"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Button
              type="submit"
              className={`flex-1 h-14 text-lg ${
                direction === "INWARD" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-orange-600 hover:bg-orange-700"
              }`}
              disabled={loading || !qty}
              data-testid="store-submit-btn"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : `Submit ${direction}`}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ------------------ Step 7: Success ------------------
function SuccessStep({ entry, onNext }) {
  return (
    <Card className="border-emerald-200 shadow-sm bg-emerald-50" data-testid="store-success-card">
      <CardContent className="p-8 space-y-6 text-center">
        <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-emerald-600 text-white mx-auto">
          <CheckCircle2 className="h-10 w-10" />
        </div>
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold text-emerald-900">Logged successfully</h2>
          <p className="text-emerald-700">
            <span className="font-mono">{entry?.entry_no}</span> — {entry?.item_name}
          </p>
          <p className="text-emerald-700 text-lg">
            {entry?.direction} · {entry?.quantity} {entry?.unit}
          </p>
          {entry?.warning && (
            <p className="text-orange-700 text-sm mt-3">⚠ {entry.warning}</p>
          )}
        </div>
        <Button
          onClick={onNext}
          className="h-14 text-lg w-full max-w-sm mx-auto"
          data-testid="store-next-entry-btn"
        >
          Log another entry
        </Button>
      </CardContent>
    </Card>
  );
}

// ------------------ shared ------------------
function DirectionBadge({ direction }) {
  const isInward = direction === "INWARD";
  const Icon = isInward ? ArrowDown : ArrowUp;
  const cls = isInward
    ? "bg-emerald-100 text-emerald-800"
    : "bg-orange-100 text-orange-800";
  return (
    <div className={`inline-flex items-center gap-2 rounded-full ${cls} px-4 py-1.5 text-sm font-medium`}
         data-testid="store-direction-badge">
      <Icon className="h-4 w-4" /> {direction}
    </div>
  );
}

// ------------------ Live Inventory (full-page) ------------------
function LiveInventoryPage({ canEditInventory, canDeleteInventory, canManageEntries: _rec, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-white overflow-y-auto"
      data-testid="store-live-inventory-page"
    >
      <div className="border-b border-slate-200 sticky top-0 bg-white z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Store</p>
              <h2 className="font-heading text-lg font-bold text-slate-900 truncate">Live Inventory</h2>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="store-live-close">
            <X className="h-4 w-4 mr-1" /> Close
          </Button>
        </div>
      </div>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4">
        <LiveInventoryFull canEdit={canEditInventory} canDelete={canDeleteInventory} />
      </div>
    </div>
  );
}

// Inline picker used by the Outward flow — same look & feel as the full page
// but headed with "Which item are you taking?" and rows are clickable.
function PickFromLiveInventoryStep({ canEdit, canDelete, onBack, onPick }) {
  return (
    <div className="space-y-3" data-testid="pick-from-live-inv-step">
      <div className="rounded-sm border border-slate-200 bg-orange-50 px-3 py-2 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-orange-800">
        <ArrowUp className="h-4 w-4" /> Outward
      </div>
      <h2 className="font-heading text-2xl font-bold text-slate-900">Which item are you taking?</h2>
      <LiveInventoryFull canEdit={canEdit} canDelete={canDelete} onPick={onPick} />
      <div className="pt-2">
        <Button variant="outline" onClick={onBack} data-testid="pick-back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
      </div>
    </div>
  );
}

function LiveInventoryFull({ canEdit, canDelete, onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("__all");
  const [sortBy, setSortBy] = useState("name");   // name|qty|last_in|last_out|category
  const [sortDir, setSortDir] = useState("asc");
  const [inFrom, setInFrom] = useState("");
  const [inTo, setInTo] = useState("");
  const [outFrom, setOutFrom] = useState("");
  const [outTo, setOutTo] = useState("");
  const [collapsed, setCollapsed] = useState({});     // { category: true }
  const [zoomPhoto, setZoomPhoto] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deletingRow, setDeletingRow] = useState(null);   // for in-flight per-row spinner
  const [requests, setRequests] = useState([]);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const resolvePhoto = (u) => (u && u.startsWith("data:") ? u : `${BACKEND}${u}`);

  const reloadRequests = async () => {
    if (!canDelete) return;
    try {
      const { data } = await ax.get(`${API}/store/delete-requests`, { params: { status: "pending" } });
      setRequests(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };
  useEffect(() => { reloadRequests(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [canDelete]);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await ax.get(`${API}/store/items`);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load inventory");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const onDeleteRow = async (row) => {
    if (!window.confirm(`Delete "${row.name}" from live inventory? This cannot be undone.`)) return;
    setDeletingRow(row.id);
    try {
      const { data } = await ax.delete(`${API}/store/items/${row.id}`);
      if (data.status === "deleted") {
        toast.success(`"${row.name}" deleted`);
        reload();
      } else {
        toast.info(`Deletion pending director approval`);
        reloadRequests();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingRow(null);
    }
  };
  const onApproveReq = async (r) => {
    try { await ax.post(`${API}/store/delete-requests/${r.id}/approve`); toast.success("Approved & deleted"); reload(); reloadRequests(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Approve failed"); }
  };
  const onRejectReq = async (r) => {
    const note = window.prompt("Reason to reject (shown to requester):", "") || "";
    try { await ax.post(`${API}/store/delete-requests/${r.id}/reject`, { note }); toast.success("Rejected"); reloadRequests(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Reject failed"); }
  };

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => (i.category || "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const inDate = (iso) => (iso ? iso.slice(0, 10) : "");
  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    return items.filter((it) => {
      if (category !== "__all") {
        if (category === "__none") { if ((it.category || "").trim()) return false; }
        else if ((it.category || "") !== category) return false;
      }
      if (qn) {
        const hay = [
          it.name || "",
          it.category || "",
          it.unit || "",
          ...((it.additional_specs || []).flatMap((s) => [s.type, s.unit, s.value])),
        ].join(" ").toLowerCase();
        if (!hay.includes(qn)) return false;
      }
      const li = inDate(it.last_inward_at);
      const lo = inDate(it.last_outward_at);
      if (inFrom && (!li || li < inFrom)) return false;
      if (inTo   && (!li || li > inTo))   return false;
      if (outFrom && (!lo || lo < outFrom)) return false;
      if (outTo   && (!lo || lo > outTo))   return false;
      return true;
    });
  }, [items, q, category, inFrom, inTo, outFrom, outTo]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const key = (it) => {
      switch (sortBy) {
        case "qty":      return Number(it.qty_on_hand ?? 0);
        case "last_in":  return it.last_inward_at || "";
        case "last_out": return it.last_outward_at || "";
        case "category": return (it.category || "").toLowerCase();
        default:         return (it.name || "").toLowerCase();
      }
    };
    return [...filtered].sort((a, b) => {
      const ka = key(a), kb = key(b);
      if (ka < kb) return -1 * dir;
      if (ka > kb) return  1 * dir;
      return 0;
    });
  }, [filtered, sortBy, sortDir]);

  const grouped = useMemo(() => {
    const g = new Map();
    sorted.forEach((it) => {
      const c = (it.category || "").trim() || "Uncategorised";
      if (!g.has(c)) g.set(c, []);
      g.get(c).push(it);
    });
    return Array.from(g.entries());
  }, [sorted]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };
  const arrow = (col) => (sortBy === col ? (sortDir === "asc" ? " ↑" : " ↓") : "");

  const reset = () => {
    setQ(""); setCategory("__all");
    setInFrom(""); setInTo(""); setOutFrom(""); setOutTo("");
    setSortBy("name"); setSortDir("asc");
  };
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

  return (
    <div className="space-y-4" data-testid="store-live-inventory-full">
      {/* Filter bar */}
      <div className="rounded-sm border border-slate-200 bg-slate-50/50 p-3 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name, category, spec value…"
                className="pl-8 h-9"
                data-testid="live-search-input"
              />
            </div>
          </div>
          <div className="min-w-[180px]">
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="live-category-filter"
              className="w-full h-9 px-2 border border-slate-300 rounded-sm text-sm bg-white"
            >
              <option value="__all">All</option>
              <option value="__none">— Uncategorised —</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Last Inward</label>
            <div className="flex items-center gap-1">
              <input type="date" value={inFrom} onChange={(e) => setInFrom(e.target.value)}
                className="h-9 px-1.5 border border-slate-300 rounded-sm text-xs" data-testid="live-in-from" />
              <span className="text-slate-400 text-xs">→</span>
              <input type="date" value={inTo} onChange={(e) => setInTo(e.target.value)}
                className="h-9 px-1.5 border border-slate-300 rounded-sm text-xs" data-testid="live-in-to" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Last Outward</label>
            <div className="flex items-center gap-1">
              <input type="date" value={outFrom} onChange={(e) => setOutFrom(e.target.value)}
                className="h-9 px-1.5 border border-slate-300 rounded-sm text-xs" data-testid="live-out-from" />
              <span className="text-slate-400 text-xs">→</span>
              <input type="date" value={outTo} onChange={(e) => setOutTo(e.target.value)}
                className="h-9 px-1.5 border border-slate-300 rounded-sm text-xs" data-testid="live-out-to" />
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={reset} data-testid="live-reset">Reset</Button>
        </div>
        <p className="text-[11px] text-slate-500">
          {loading ? "Loading…" : `${sorted.length} item${sorted.length === 1 ? "" : "s"} · ${grouped.length} categor${grouped.length === 1 ? "y" : "ies"}`}
          {canEdit && <span className="ml-3 text-emerald-700 font-semibold">Edit rights granted</span>}
          {canDelete && <span className="ml-3 text-red-700 font-semibold">Delete rights granted</span>}
        </p>
      </div>

      {canDelete && requests.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-sm p-3" data-testid="delete-requests-panel">
          <p className="text-[10px] tracking-[0.25em] uppercase text-amber-900 font-semibold mb-2">Pending Delete Requests · {requests.length}</p>
          <div className="space-y-1.5">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center gap-3 flex-wrap text-xs" data-testid={`delete-req-${r.id}`}>
                <span className="font-semibold text-slate-900">{r.item_name}</span>
                <span className="text-slate-500">· {r.item_unit} · {r.item_category || "Uncategorised"}</span>
                <span className="text-slate-500">requested by {r.requested_by_name} ({r.requested_by})</span>
                <span className="text-slate-400 font-mono">{r.requested_at?.slice(0, 16)}</span>
                <div className="ml-auto flex items-center gap-1">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7" onClick={() => onApproveReq(r)} data-testid={`delete-req-approve-${r.id}`}>Approve &amp; Delete</Button>
                  <Button size="sm" variant="outline" className="h-7 border-red-300 text-red-700 hover:bg-red-50" onClick={() => onRejectReq(r)} data-testid={`delete-req-reject-${r.id}`}>Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="p-6 flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
      ) : sorted.length === 0 ? (
        <div className="p-8 text-center text-slate-500 border border-dashed border-slate-200 rounded-sm">No items match the filters.</div>
      ) : (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white text-[10px] tracking-widest uppercase">
              <tr>
                <th className="text-left px-3 py-2 font-semibold cursor-pointer" onClick={() => toggleSort("name")} data-testid="sort-name">
                  Item{arrow("name")}
                </th>
                <th className="text-left px-3 py-2 font-semibold cursor-pointer" onClick={() => toggleSort("category")} data-testid="sort-category">
                  Category{arrow("category")}
                </th>
                <th className="text-left px-3 py-2 font-semibold">Specs</th>
                <th className="text-left px-3 py-2 font-semibold">Unit</th>
                <th className="text-right px-3 py-2 font-semibold cursor-pointer" onClick={() => toggleSort("qty")} data-testid="sort-qty">
                  Qty on hand{arrow("qty")}
                </th>
                <th className="text-left px-3 py-2 font-semibold cursor-pointer" onClick={() => toggleSort("last_in")} data-testid="sort-last-in">
                  Last inward{arrow("last_in")}
                </th>
                <th className="text-left px-3 py-2 font-semibold cursor-pointer" onClick={() => toggleSort("last_out")} data-testid="sort-last-out">
                  Last outward{arrow("last_out")}
                </th>
                <th className="text-left px-3 py-2 font-semibold">Location</th>
                <th className="text-center px-3 py-2 font-semibold">Photo</th>
                {canEdit && <th className="text-center px-3 py-2 font-semibold">Edit</th>}
                {canDelete && <th className="text-center px-3 py-2 font-semibold">Delete</th>}
              </tr>
            </thead>
            {grouped.map(([cat, rows]) => (
              <tbody key={`grp-${cat}`}>
                <tr className="bg-slate-100" data-testid={`live-group-${cat}`}>
                    <td colSpan={(canEdit ? 1 : 0) + (canDelete ? 1 : 0) + 9} className="px-3 py-1.5">
                      <button
                        type="button"
                        onClick={() => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }))}
                        className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-700 hover:text-slate-900"
                        data-testid={`live-group-toggle-${cat}`}
                      >
                        {collapsed[cat] ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        {cat}
                        <span className="ml-1.5 text-slate-500 font-mono">({rows.length})</span>
                      </button>
                    </td>
                  </tr>
                  {!collapsed[cat] && rows.map((r) => (
                    <tr
                      key={r.id}
                      className={`border-t border-slate-100 hover:bg-slate-50 ${onPick ? "cursor-pointer" : ""}`}
                      onClick={onPick ? (e) => {
                        // Ignore clicks on action-column buttons (edit / delete / photo zoom)
                        const t = e.target;
                        if (t.closest && t.closest("button")) return;
                        onPick(r);
                      } : undefined}
                      data-testid={`live-row-${r.id}`}
                    >
                      <td className="px-3 py-2 font-semibold text-slate-900">{r.name}</td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{r.category || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-700">
                        {r.additional_specs?.length ? (
                          <div className="flex flex-wrap gap-1">
                            {r.additional_specs.map((s, i) => (
                              <span key={i} className="inline-flex items-center rounded-sm bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-[10px] font-mono">
                                {s.type}: {s.value}{s.unit ? ` ${s.unit}` : ""}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-xs font-mono">{r.unit}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        <span className={Number(r.qty_on_hand) <= 0 ? "text-red-700 font-bold" : "text-slate-900 font-semibold"}>
                          {formatBalance(r.qty_on_hand ?? 0)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-700 text-[11px] font-mono">{fmtDate(r.last_inward_at)}</td>
                      <td className="px-3 py-2 text-slate-700 text-[11px] font-mono">{fmtDate(r.last_outward_at)}</td>
                      <td className="px-3 py-2 text-slate-700 text-[11px]">{r.last_location || "—"}</td>
                      <td className="px-3 py-2 text-center">
                        {r.last_photo_thumb_b64 ? (
                          <button
                            type="button"
                            onClick={() => setZoomPhoto(resolvePhoto(r.last_photo_thumb_b64))}
                            data-testid={`live-photo-${r.id}`}
                          >
                            <img
                              src={resolvePhoto(r.last_photo_thumb_b64)}
                              alt={r.name}
                              className="h-9 w-9 object-cover rounded-sm border border-slate-200 mx-auto hover:ring-2 hover:ring-slate-400"
                            />
                          </button>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-3 py-2 text-center">
                          <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`live-edit-${r.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      )}
                      {canDelete && (
                        <td className="px-3 py-2 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={deletingRow === r.id}
                            onClick={() => onDeleteRow(r)}
                            data-testid={`live-delete-${r.id}`}
                            className="text-red-700 hover:text-red-900 hover:bg-red-50"
                          >
                            {deletingRow === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            ))}
          </table>
        </div>
      )}

      {zoomPhoto && (
        <div
          onClick={() => setZoomPhoto(null)}
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4 cursor-pointer"
          data-testid="live-photo-zoom"
        >
          <img src={zoomPhoto} alt="preview" className="max-h-[90vh] max-w-[90vw] object-contain rounded-sm" />
        </div>
      )}

      {editing && (
        <EditItemDialog
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function EditItemDialog({ item, onClose, onSaved }) {
  const [name, setName] = useState(item.name || "");
  const [unit, setUnit] = useState(item.unit || "");
  const [category, setCategory] = useState(item.category || "");
  const [specs, setSpecs] = useState(item.additional_specs || []);
  const [active, setActive] = useState(item.active !== false);
  const [saving, setSaving] = useState(false);
  const [cats, setCats] = useState([]);
  const [specTypes, setSpecTypes] = useState([]);
  const [specUnits, setSpecUnits] = useState([]);

  useEffect(() => {
    const load = async (t, s) => {
      try {
        const { data } = await ax.get(`${API}/masters`, { params: { master_type: t, status: "active" } });
        s((data || []).filter((m) => m.active !== false).map((m) => m.name));
      } catch { /* silent */ }
    };
    load("store_category", setCats);
    load("store_spec_type", setSpecTypes);
    load("store_spec_unit", setSpecUnits);
  }, []);

  const addSpec = () => setSpecs((s) => [...s, { type: "", unit: "", value: "" }]);
  const rmSpec  = (i) => setSpecs((s) => s.filter((_, idx) => idx !== i));
  const updSpec = (i, k, v) => setSpecs((s) => s.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const save = async () => {
    if (!name.trim() || !unit.trim()) {
      toast.error("Name and unit are required");
      return;
    }
    setSaving(true);
    try {
      const cleanSpecs = specs.filter((s) => (s.value || "").toString().trim());
      await ax.patch(`${API}/store/items/${item.id}`, {
        name: name.trim(),
        unit: unit.trim(),
        category: category.trim(),
        additional_specs: cleanSpecs,
        active,
      });
      toast.success("Item updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[65] bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="store-edit-item-dialog"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-sm border border-slate-200 p-5 space-y-4 mt-10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Edit item</p>
            <h3 className="font-heading text-lg font-bold text-slate-900">{item.name}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label>Item name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-item-name" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Unit</Label>
              <Input value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="edit-item-unit" className="mt-1" />
            </div>
            <div>
              <Label>Category</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="edit-item-category"
                className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm bg-white">
                <option value="">— None —</option>
                {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                {category && !cats.includes(category) && <option value={category}>{category} (legacy)</option>}
              </select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Additional specs</Label>
              <Button type="button" size="sm" variant="outline" onClick={addSpec} data-testid="edit-item-add-spec">+ Add spec</Button>
            </div>
            {specs.length === 0 && <p className="text-xs text-slate-500 mt-1">No specs. Add e.g. Thickness · mm · 120.</p>}
            <div className="space-y-2 mt-2">
              {specs.map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2" data-testid={`edit-item-spec-${i}`}>
                  <select value={r.type} onChange={(e) => updSpec(i, "type", e.target.value)}
                    className="h-9 px-2 border border-slate-300 rounded-sm text-sm bg-white">
                    <option value="">Type…</option>
                    {specTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    {r.type && !specTypes.includes(r.type) && <option value={r.type}>{r.type} (legacy)</option>}
                  </select>
                  <select value={r.unit} onChange={(e) => updSpec(i, "unit", e.target.value)}
                    className="h-9 px-2 border border-slate-300 rounded-sm text-sm bg-white">
                    <option value="">Unit…</option>
                    {specUnits.map((u) => <option key={u} value={u}>{u}</option>)}
                    {r.unit && !specUnits.includes(r.unit) && <option value={r.unit}>{r.unit} (legacy)</option>}
                  </select>
                  <Input value={r.value} onChange={(e) => updSpec(i, "value", e.target.value)}
                    placeholder="Value" className="h-9" />
                  <button type="button" onClick={() => rmSpec(i)} className="h-9 w-9 text-red-600 hover:bg-red-50 rounded-sm">×</button>
                </div>
              ))}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="edit-item-active" />
            Active (uncheck to hide from all pickers)
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="edit-item-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Inline (tab) version of the recent-entries table — same behaviour as the
// old modal but rendered without its own overlay chrome.
function RecentEntriesInline() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await ax.get(`${API}/store/entries`, { params: { limit: 200 } });
      setEntries(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="border border-slate-200 rounded-sm" data-testid="recent-entries-inline">
      {loading ? (
        <div className="p-6 flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-white text-[10px] tracking-widest uppercase">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">When</th>
              <th className="text-left px-3 py-2 font-semibold">Dir</th>
              <th className="text-left px-3 py-2 font-semibold">Item</th>
              <th className="text-right px-3 py-2 font-semibold">Qty</th>
              <th className="text-left px-3 py-2 font-semibold">Location</th>
              <th className="text-left px-3 py-2 font-semibold">Worker</th>
              <th className="text-left px-3 py-2 font-semibold">Note</th>
              <th className="text-center px-3 py-2 font-semibold">Edit</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-500 py-6">No entries yet.</td></tr>
            )}
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`recent-row-${e.id}`}>
                <td className="px-3 py-2 text-[11px] font-mono text-slate-600">{new Date(e.created_at).toLocaleString()}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${e.direction === "INWARD" ? "bg-emerald-100 text-emerald-800" : "bg-orange-100 text-orange-800"}`}>{e.direction}</span>
                </td>
                <td className="px-3 py-2 text-slate-900">{e.item_name}</td>
                <td className="px-3 py-2 text-right font-mono">{e.quantity} <span className="text-slate-400">{e.unit}</span></td>
                <td className="px-3 py-2 text-slate-700 text-[11px]">{e.location || "—"}</td>
                <td className="px-3 py-2 text-slate-700 text-[11px]">{e.employee_name || e.employee_code}</td>
                <td className="px-3 py-2 text-slate-600 text-[11px] max-w-[180px] truncate" title={e.note || ""}>{e.note || "—"}</td>
                <td className="px-3 py-2 text-center">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(e)} data-testid={`recent-edit-${e.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <EditEntryDialog
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}


// ------------------ Director: recent entries + edit ------------------
function RecentEntriesModal({ onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // an entry object

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await ax.get(`${API}/store/entries`, { params: { limit: 100 } });
      setEntries(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="store-history-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl bg-white rounded-sm border border-slate-200 mt-8"
      >
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Director tools</p>
            <h3 className="font-heading text-lg font-bold text-slate-900">Recent entries</h3>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="store-history-close">
            <X className="h-5 w-5" />
          </Button>
        </div>
        {loading ? (
          <div className="p-6 flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-[10px] tracking-widest uppercase text-slate-500">
                  <th className="text-left px-3 py-2 font-semibold">When</th>
                  <th className="text-left px-3 py-2 font-semibold">Dir</th>
                  <th className="text-left px-3 py-2 font-semibold">Item</th>
                  <th className="text-right px-3 py-2 font-semibold">Qty</th>
                  <th className="text-left px-3 py-2 font-semibold">Location</th>
                  <th className="text-left px-3 py-2 font-semibold">Worker</th>
                  <th className="text-left px-3 py-2 font-semibold">Note</th>
                  <th className="text-center px-3 py-2 font-semibold">Edit</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-slate-500 py-6">No entries yet.</td></tr>
                )}
                {entries.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`store-history-row-${e.id}`}>
                    <td className="px-3 py-2 text-[11px] font-mono text-slate-600">{new Date(e.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${e.direction === "INWARD" ? "bg-emerald-100 text-emerald-800" : "bg-orange-100 text-orange-800"}`}>{e.direction}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-900">{e.item_name}</td>
                    <td className="px-3 py-2 text-right font-mono">{e.quantity} <span className="text-slate-400">{e.unit}</span></td>
                    <td className="px-3 py-2 text-slate-700 text-[11px]">{e.location || "—"}</td>
                    <td className="px-3 py-2 text-slate-700 text-[11px]">{e.employee_name || e.employee_code}</td>
                    <td className="px-3 py-2 text-slate-600 text-[11px] max-w-[180px] truncate" title={e.note || ""}>{e.note || "—"}</td>
                    <td className="px-3 py-2 text-center">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(e)} data-testid={`store-edit-btn-${e.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing && (
        <EditEntryDialog
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function EditEntryDialog({ entry, onClose, onSaved }) {
  const [qty, setQty] = useState(String(entry.quantity ?? ""));
  const [loc, setLoc] = useState(entry.location || "");
  const [note, setNote] = useState(entry.note || "");
  const [saving, setSaving] = useState(false);
  const isInward = entry.direction === "INWARD";

  const save = async () => {
    const n = parseFloat(qty);
    if (!(n > 0)) { toast.error("Quantity must be > 0"); return; }
    if (isInward && !loc.trim()) { toast.error("Location is required for inward entries"); return; }
    setSaving(true);
    try {
      await ax.patch(`${API}/store/entries/${entry.id}`, {
        quantity: n,
        note: note,
        location: loc,
      });
      toast.success("Entry updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      data-testid="store-edit-dialog"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-sm border border-slate-200 p-5 space-y-4">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Edit entry · {entry.entry_no}</p>
          <h3 className="font-heading text-lg font-bold text-slate-900">{entry.item_name}</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {entry.direction} · logged by {entry.employee_name || entry.employee_code}
          </p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold">Quantity ({entry.unit})</Label>
          <Input type="number" step="any" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="store-edit-qty" />
        </div>
        {isInward && (
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Location <span className="text-red-600">*</span></Label>
            <Input value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="store-edit-location" />
          </div>
        )}
        <div className="space-y-2">
          <Label className="text-xs font-semibold">Note</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="store-edit-note" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="store-edit-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

